import { useState } from 'react';
import { Mic, PlayArrow } from '@mui/icons-material';
import { Badge, Button } from '@mui/material';
import { useDispatch } from 'react-redux';

import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { adminV2Api, useGetTranscriptDetailsQuery } from '../../../store/api';
import { formatDateTime } from '../../../utils/formatting';
import { ExplorableId } from '../../explorable-id';
import { num, str } from '../field-access';
import type { ResourceDetailSlotProps } from '../types';
import { statusTone } from './status-tone';

const INTAKE_TEMPLATE = 'p:21fd6721-0be9-48cf-9de7-b7f341d15940';

/** "Recovered Note (MM-DD-YYYY HH:MM)" in the clinician's timezone. */
function noteTitleFor(startTime: string, timezone?: string): string {
  const formatted = new Date(startTime).toLocaleString('en-US', {
    timeZone: timezone || 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `Recovered Note (${formatted})`;
}

/**
 * Transcript identity band, plus the one action this resource carries.
 *
 * Identity comes from the registry record; "Generate Note" needs the
 * clinician's template list and timezone, which only the expansion has, so
 * this shares that query with the sections slot (RTK Query dedupes it).
 */
export function TranscriptionHeader({ data, resourceId, scope }: ResourceDetailSlotProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const { getAccessToken } = useAccessToken();
  const dispatch = useDispatch();

  const { data: expand, refetch } = useGetTranscriptDetailsQuery(
    { transcriptionId: resourceId },
    { skip: !resourceId },
  );

  const jobStatus = str(data, 'job_status');
  const deletedAt = str(data, 'deleted_at');
  const startTimestamp = num(data, 'start_timestamp');
  const chunkCount = expand?.transcription?.chunk_count;
  const notesCreated = expand?.transcription?.notes_created;
  const provider = expand?.provider as
    | {
        provider_id?: string;
        templates?: { template_id: string }[];
        default_timezone?: string;
      }
    | undefined;
  const userId = str(data, 'user_id') ?? provider?.provider_id;

  const startNoteProcessing = async () => {
    const startTime = expand?.transcription?.start_time;
    if (!userId || !startTime) return;
    setIsProcessing(true);
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/noteProcessingSession/startNoteProcessingForUser',
        accessToken,
        body: {
          target_user_id: userId,
          transcription_session_id: resourceId,
          note_title: noteTitleFor(startTime, provider?.default_timezone),
          template_id: provider?.templates?.[0]?.template_id || INTAKE_TEMPLATE,
        },
      });

      if (!response.ok) {
        setIsProcessing(false);
        return;
      }

      // The note lands asynchronously, so invalidate and poll until the
      // count moves rather than claiming success immediately.
      const before = expand?.transcription?.notes_created ?? 0;
      dispatch(
        adminV2Api.util.invalidateTags([
          { type: 'Transcription', id: resourceId },
          { type: 'Note', id: userId },
        ]),
      );

      const poll = (attempt = 1) => {
        setTimeout(
          async () => {
            try {
              const result = await refetch();
              const updated = result.data?.transcription?.notes_created ?? 0;
              if (updated > before || attempt >= 10) {
                setIsProcessing(false);
                return;
              }
              poll(attempt + 1);
            } catch {
              setIsProcessing(false);
            }
          },
          Math.min(1000 * attempt, 5000),
        );
      };
      poll();
    } catch {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Mic className="mr-2 h-6 w-6" />
            Transcript
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {startTimestamp
              ? formatDateTime(new Date(startTimestamp * 1000).toISOString())
              : '—'}
            {chunkCount != null ? ` · ${chunkCount} chunks` : ''}
            {notesCreated != null ? ` · ${notesCreated} notes created` : ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <ExplorableId
              label="transcript"
              value={resourceId}
              organizationId={scope.organizationId}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {jobStatus && (
            <Badge color={statusTone(jobStatus)}>
              {jobStatus.replace('_', ' ')}
            </Badge>
          )}
          {deletedAt && (
            <span className="rounded bg-red-100 px-2 py-0.5 text-sm font-medium text-red-700">
              Deleted {formatDateTime(deletedAt)}
            </span>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={startNoteProcessing}
            disabled={isProcessing || !userId}
            startIcon={<PlayArrow />}
          >
            {isProcessing ? 'Submitting…' : 'Generate Note'}
          </Button>
        </div>
      </div>
    </div>
  );
}
